import { Tag, Tooltip } from 'antd';
import type { ColumnType, FilterValue } from 'antd/es/table/interface';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale/ru';

import type {
  TEnrolledSectionParticipant,
  IEnrolledSectionStats,
} from '@/api/endpoints/schools/schoolSelectionPanel/enrolled/types';
import { EEnrolledSectionParticipantStatus } from '@/api/endpoints/schools/schoolSelectionPanel/enrolled/types';
import { DATE_FORMATTERS } from '@/shared/consts';

import { OptionsCell } from './_components/OptionsCell/OptionsCell';
import { STUDENT_STATUS_TO_TAG_SETTINGS } from './consts';
import styles from './EnrolledListPage.module.scss';

const formatPhone = (phone: string) =>
  `${phone.slice(0, 2)} (${phone.slice(2, 5)}) ${phone.slice(5, 8)}-${phone.slice(8, 10)}-${phone.slice(10, 12)}`;

export const getColumnsData = ({
  filters,
  openExpulsionModalCb,
  setItemToOperate,
}: {
  filters: Record<string, FilterValue | null>;
  openExpulsionModalCb: VoidFunction;
  setItemToOperate: (data: TEnrolledSectionParticipant | null) => void;
}): ColumnType<TEnrolledSectionParticipant>[] => [
  {
    title: 'ФИО',
    dataIndex: ['name'],
    key: 'name',
    ellipsis: true,
  },
  {
    title: 'Email',
    dataIndex: ['email'],
    key: 'email',
    ellipsis: true,
  },
  {
    title: 'Телефон',
    dataIndex: ['phone'],
    key: 'phone',
    ellipsis: true,
    render: (_, { phone }) => (phone ? formatPhone(phone) : '-'),
  },
  {
    title: 'Дата зачисления',
    dataIndex: 'accepted_at',
    key: 'accepted_at',
    ellipsis: true,
    render: (_, { accepted_at: acceptedAt }) =>
      format(acceptedAt, DATE_FORMATTERS.DAY_SHORT_MONTH_YEAR, { locale: ru }),
  },
  {
    title: 'Статус',
    dataIndex: 'status',
    key: 'status',
    ellipsis: true,
    filteredValue: filters.status || null,
    filters: [
      {
        text: STUDENT_STATUS_TO_TAG_SETTINGS[EEnrolledSectionParticipantStatus.Accepted].text,
        value: EEnrolledSectionParticipantStatus.Accepted,
      },
      {
        text: STUDENT_STATUS_TO_TAG_SETTINGS[EEnrolledSectionParticipantStatus.Expelled].text,
        value: EEnrolledSectionParticipantStatus.Expelled,
      },
    ],
    render: (_, { status }) => (
      <Tag
        className={styles.tag}
        color={STUDENT_STATUS_TO_TAG_SETTINGS[status].color}
        icon={STUDENT_STATUS_TO_TAG_SETTINGS[status].icon}
        variant="outlined"
      >
        {STUDENT_STATUS_TO_TAG_SETTINGS[status].text}
      </Tag>
    ),
  },
  {
    title: 'Причина отчисления',
    dataIndex: 'expulsion_reason',
    key: 'expulsion_reason',
    ellipsis: true,
    render: (_, { expulsion_reason: expulsionReason, expelled_at: expelledAt }) =>
      expulsionReason ? (
        <>
          <Tooltip title={expulsionReason}>
            <p className={styles['expulsion-reason']}>{expulsionReason}</p>
          </Tooltip>
          <span className={styles['expulsion-date']}>
            {format(expelledAt, DATE_FORMATTERS.DAY_SHORT_MONTH_YEAR, { locale: ru })}
          </span>
        </>
      ) : (
        '-'
      ),
  },
  {
    title: '',
    dataIndex: 'options',
    key: 'options',
    width: 64,
    fixed: 'right',
    render: (_, participantData) =>
      participantData.status !== EEnrolledSectionParticipantStatus.Expelled ? (
        <OptionsCell
          openExpulsionModalCb={() => {
            setItemToOperate(participantData);
            openExpulsionModalCb();
          }}
        />
      ) : null,
  },
];

export const formatEnrolledStats = (enrolledStats: IEnrolledSectionStats | undefined) => {
  if (!enrolledStats) {
    return;
  }

  const { total, accepted, expelled } = enrolledStats;

  return [
    {
      id: 1,
      title: 'Всего студентов',
      value: total,
    },
    {
      id: 2,
      title: 'Зачислено',
      value: accepted,
    },
    {
      id: 3,
      title: 'Отчислено',
      value: expelled,
    },
  ];
};
